import { useState } from "react";
import clsx from "clsx";

import {
  FieldValues,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { PhoneInput, PhoneInputProps } from "react-international-phone";
import "react-international-phone/style.css";

export type TPhoneInputProps<T extends FieldValues> = UseControllerProps<T> &
  PhoneInputProps;

const TPhoneInput = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  className,
  ...props
}: TPhoneInputProps<T>) => {
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <div className={clsx("w-full", className)}>
      <PhoneInput
        defaultCountry="in"
        countrySelectorStyleProps={{
          buttonClassName: clsx(
            "!h-[50px] !w-full !rounded-none !border-0 !border-b !border-b-primary-500 !bg-primary-50 !px-4 !py-2 focus:!border-b-primary-900 focus:!outline-none",
            fieldState.error?.message &&
              isInputFocused &&
              "!border !border-l !border-r-0 !border-t !border-b-red-500 !border-l-red-500 !border-t-red-500",
            isInputFocused && "!border-b-primary-900",
          ),
          dropdownStyleProps: {
            className: "!bg-primary-50",
            listItemClassName: "!px-4 !py-2 hover:!bg-primary-100",
          },
        }}
        inputClassName={clsx(
          "!h-[50px] !w-full !rounded-none !border-b !border-l-0 !border-x-transparent !border-b-primary-500 !border-t-transparent !bg-primary-50 !py-4 !pl-0 !pr-4 !text-sm !text-black-900 !placeholder-gray-500 focus:!border-b-primary-900",
          fieldState.error?.message &&
            "focus:!border focus:!border-l-0 focus:!border-red-500",
        )}
        value={value}
        onChange={(e, v) => {
          fieldOnChange(e);
          onChange?.(e, v);
        }}
        {...field}
        {...props}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
      />
      {fieldState.error?.message && (
        <span className="mt-1 text-sm leading-3 text-red-500">
          {fieldState.error?.message}
        </span>
      )}
    </div>
  );
};

export default TPhoneInput;
